<template>
  <div class="output-params-page">
    <a-page-header
      :title="`出参列表 - ${schemeName || 'AI调用流程'}`"
      @back="handleBack"
    >
      <template #extra>
        <a-space>
          <a-tag color="blue" style="font-size: 14px; padding: 4px 12px;">
            当前版本: {{ currentVersion }}
          </a-tag>
          <span v-if="updatedAt" style="color: #999; font-size: 13px;">
            最后更新: {{ formatDate(updatedAt) }}
          </span>
          <a-button @click="showVersionHistory = true">
            <template #icon><HistoryOutlined /></template>
            版本历史
          </a-button>
          <a-button type="link" @click="showRequirementDoc = true">
            <template #icon><QuestionCircleOutlined /></template>
            本页需求
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <div class="steps-container">
        <a-collapse v-model:activeKey="expandedSteps" :bordered="false">
          <a-collapse-panel
            v-for="step in steps"
            :key="step.step_order"
            :header="`步骤${step.step_order}: ${step.step_name}`"
            class="step-panel"
          >
            <div class="workers-list">
              <div
                v-for="worker in step.workers"
                :key="worker.worker_id"
                class="worker-card"
              >
                <div class="worker-header">
                  <div class="worker-info">
                    <span class="worker-name">{{ worker.worker_name }}</span>
                    <a-tag color="default" size="small">ID: {{ worker.worker_id }}</a-tag>
                  </div>
                  <a-button
                    type="primary"
                    size="small"
                    @click="handleEditWorker(worker.worker_id)"
                  >
                    <template #icon><EditOutlined /></template>
                    编辑
                  </a-button>
                </div>
                <div class="output-params-container">
                  <div v-if="worker.output_params" class="json-wrapper">
                    <a-button
                      class="copy-btn"
                      size="small"
                      @click="handleCopy(worker.output_params)"
                    >
                      <template #icon><CopyOutlined /></template>
                      复制
                    </a-button>
                    <pre class="json-display"><code v-html="highlightJson(worker.output_params)"></code></pre>
                  </div>
                  <div v-else class="empty-params">
                    <a-empty description="暂未定义出参" :image="Empty.PRESENTED_IMAGE_SIMPLE" />
                  </div>
                </div>
              </div>
              <div v-if="step.workers.length === 0" class="no-workers">
                <a-empty description="该步骤下暂无Worker" :image="Empty.PRESENTED_IMAGE_SIMPLE" />
              </div>
            </div>
          </a-collapse-panel>
        </a-collapse>

        <div v-if="steps.length === 0 && !loading" class="empty-steps">
          <a-empty description="暂无步骤数据" />
        </div>
      </div>
    </a-spin>

    <!-- 版本历史弹窗 -->
    <VersionHistoryModal
      v-model:visible="showVersionHistory"
      :scheme-id="schemeId"
    />

    <!-- 需求说明弹窗 -->
    <RequirementDocModal
      v-model:visible="showRequirementDoc"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message, Empty } from 'ant-design-vue';
import {
  HistoryOutlined,
  QuestionCircleOutlined,
  EditOutlined,
  CopyOutlined
} from '@ant-design/icons-vue';
import axios from 'axios';
import VersionHistoryModal from '../components/OutputParams/VersionHistoryModal.vue';
import RequirementDocModal from '../components/OutputParams/RequirementDocModal.vue';

const route = useRoute();
const router = useRouter();

const schemeId = computed(() => parseInt(route.params.schemeId));
const schemeName = ref('');
const currentVersion = ref('v1.0.0');
const updatedAt = ref(null);
const steps = ref([]);
const loading = ref(false);
const expandedSteps = ref([]);
const showVersionHistory = ref(false);
const showRequirementDoc = ref(false);

// 获取出参列表数据
const fetchData = async () => {
  loading.value = true;
  try {
    const res = await axios.get(`/api/call-scheme/${schemeId.value}/output-params`);
    if (res.data.code === 200) {
      const data = res.data.data;
      schemeName.value = data.scheme_name;
      currentVersion.value = data.current_version || 'v1.0.0';
      updatedAt.value = data.updated_at;
      steps.value = data.steps || [];
      // 默认展开所有步骤
      expandedSteps.value = steps.value.map(s => s.step_order);
    } else {
      message.error(res.data.message || '获取数据失败');
    }
  } catch (error) {
    console.error('获取出参列表失败:', error);
    message.error('获取出参列表失败');
  } finally {
    loading.value = false;
  }
};

// JSON语法高亮
const highlightJson = (obj) => {
  if (!obj) return '';
  const jsonStr = JSON.stringify(obj, null, 2);
  return jsonStr
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // 字符串值（绿色）
    .replace(/"([^"]+)":/g, '<span class="json-key">"$1"</span>:')
    // 字符串（橙色）
    .replace(/: "([^"]*)"/g, ': <span class="json-string">"$1"</span>')
    // 数字（蓝色）
    .replace(/: (\d+)/g, ': <span class="json-number">$1</span>')
    // 布尔值（紫色）
    .replace(/: (true|false)/g, ': <span class="json-boolean">$1</span>')
    // null（灰色）
    .replace(/: (null)/g, ': <span class="json-null">$1</span>');
};

// 复制到剪贴板
const handleCopy = async (obj) => {
  try {
    const jsonStr = JSON.stringify(obj, null, 2);
    await navigator.clipboard.writeText(jsonStr);
    message.success('已复制到剪贴板');
  } catch (error) {
    // 降级方案
    const textarea = document.createElement('textarea');
    textarea.value = JSON.stringify(obj, null, 2);
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    message.success('已复制到剪贴板');
  }
};

// 编辑Worker
const handleEditWorker = (workerId) => {
  // 跳转到步骤管理页面并定位到该Worker
  router.push({
    path: `/call-scheme/${schemeId.value}/steps`,
    query: { editWorker: workerId }
  });
};

// 返回
const handleBack = () => {
  router.push(`/call-scheme/${schemeId.value}/steps`);
};

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.output-params-page {
  padding: 24px;
  min-height: 100vh;
  background: #f5f5f5;
}

.steps-container {
  margin-top: 16px;
}

.step-panel {
  margin-bottom: 16px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.ant-collapse-header) {
  font-size: 16px;
  font-weight: 600;
  background: #fafafa;
}

.workers-list {
  padding: 8px;
}

.worker-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.worker-card:last-child {
  margin-bottom: 0;
}

.worker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.worker-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.worker-name {
  font-size: 15px;
  font-weight: 500;
}

.worker-info :deep(.ant-tag) {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
}

.output-params-container {
  padding: 16px;
  background: #1e1e1e;
  min-height: 100px;
}

.json-wrapper {
  position: relative;
}

.copy-btn {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
}

.copy-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

.json-display {
  margin: 0;
  padding: 12px;
  background: transparent;
  border-radius: 4px;
  overflow-x: auto;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #d4d4d4;
}

.json-display code {
  font-family: inherit;
}

:deep(.json-key) {
  color: #9cdcfe;
}

:deep(.json-string) {
  color: #ce9178;
}

:deep(.json-number) {
  color: #b5cea8;
}

:deep(.json-boolean) {
  color: #569cd6;
}

:deep(.json-null) {
  color: #808080;
}

.empty-params {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  background: #fafafa;
  border-radius: 4px;
}

.empty-params :deep(.ant-empty-description) {
  color: #999;
}

.no-workers {
  padding: 24px;
  text-align: center;
}

.empty-steps {
  padding: 48px;
  text-align: center;
  background: #fff;
  border-radius: 8px;
}
</style>

